import {
  Button as HeroButton,
  Spinner,
  type ButtonRootProps,
} from "@heroui/react";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const variantMap: Record<Variant, NonNullable<ButtonRootProps["variant"]>> = {
  primary: "primary",
  secondary: "secondary",
  outline: "outline",
  ghost: "ghost",
  danger: "danger",
};

export interface ButtonProps extends Omit<
  ButtonRootProps,
  "variant" | "size" | "isDisabled" | "children"
> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      leftIcon,
      rightIcon,
      disabled,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <HeroButton
      ref={ref}
      type={type}
      variant={variantMap[variant]}
      size={size}
      isDisabled={disabled || loading}
      className={cn(
        "transition-all duration-200 motion-reduce:transition-none",
        variant === "primary" &&
          "rounded-lg bg-gradient-to-r from-primary to-primary-2 font-bold shadow-[0_4px_14px_rgb(var(--color-primary)/0.3)] hover:-translate-y-px hover:scale-[1.02] hover:shadow-[0_6px_20px_rgb(var(--color-primary)/0.4)] active:translate-y-0 active:scale-95",
        variant === "ghost" &&
          "rounded-lg border border-line/15 bg-card/80 font-bold text-ink-2 shadow-[0_2px_6px_rgb(4_13_30/0.03)] backdrop-blur-md hover:-translate-y-px hover:scale-[1.02] hover:border-primary/25 hover:bg-card hover:text-primary active:translate-y-0 active:scale-95",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" color="current" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </HeroButton>
  ),
);
Button.displayName = "Button";
